/*
 * Scenario: rewrite-multi
 *
 * Sechs Rewrite-Vorschlaege ueber das ganze Dokument. Testet die ←/→-
 * Navigation im Review-Modus, das Multi-Overlay-Rendering im Main-Pane
 * (alle PENDING-Vorschlaege gleichzeitig sichtbar — `dimAllExcept` haelt
 * jeden Anker beleuchtet) und die Console-Statistik am Ende.
 *
 * Greift auf state.context.cursorContext.textParagraphs zu (siehe
 * buildCursorContextForMock in main.js). Wenn weniger als 2 Paragraphen
 * vorhanden sind, gibt das Scenario einen System-Fehler aus und beendet —
 * sinnvolle Vorschlaege brauchen Material.
 */

import { emit, wait } from './_helpers.js'

export const name = 'rewrite-multi'
export const description = 'Six rewrite proposals across the document — exercises ←/→ navigation.'

const REWRITES = [
    'It was a grey November morning. The mist lay heavy on the hills and pressed against the windows like a soft, cold hand.',
    'She paused at the threshold, listening — the silence inside the cottage was the deeper kind, the kind that holds something back.',
    'A long shadow stretched across the kitchen floor and pointed, almost politely, toward the empty kettle on the stove.',
    'On the table lay a single envelope, unaddressed, sealed in dark wax that had cracked but not broken.',
    'She heard footsteps then — slow, deliberate, coming up the gravel path that no one had used since the funeral.',
    'For a moment she did not move. The house, she thought, had been waiting for this all along.'
]

const REASONS = [
    'Mood sharpened — sensory detail added.',
    'Tightened pacing; ambiguity preserved.',
    'Concrete imagery beats abstract description.',
    'Plants the envelope as a Chekhov gun.',
    'Restores subtext lost in earlier draft.',
    'Stronger close — leans into inevitability.'
]

export async function run(state) {
    await emit(state, 'session', { sessionId: 'mock-rewrite-multi' }, 0)
    await emit(state, 'thinking', { text: 'Reading the section. I see a few places to tighten…' }, 300)
    await emit(state, 'thinkingDone', {}, 200)
    await emit(state, 'toolCall', { toolCallId: 'tu_read_multi', tool: 'read_section', input: {} }, 150)
    await emit(state, 'toolResult', { toolCallId: 'tu_read_multi', tool: 'read_section', summary: 'section read' }, 200)

    const paras = state.context?.cursorContext?.textParagraphs || []
    if (paras.length === 0) {
        await emit(state, 'delta', { text: 'No text paragraphs to rewrite.' }, 100)
        await emit(state, 'done', { usage: {} }, 100)
        return
    }

    // So viele Vorschlaege wie REWRITES Material hat, aber nicht mehr als
    // verfuegbare Paragraphen. So ist das Scenario auch in kleinen Test-
    // Dokumenten sinnvoll.
    const count = Math.min(REWRITES.length, paras.length)
    const toolCallIds = []

    for (let i = 0; i < count; i++) {
        const p = paras[i]
        const toolCallId = `tu_rwm_${i}_${Date.now().toString(36)}`
        toolCallIds.push(toolCallId)
        await emit(state, 'proposeRewrite', {
            toolCallId,
            paragraphId: p.id,
            baseVersion: typeof p.version === 'number' ? p.version : undefined,
            originalText: p.text,
            newText: REWRITES[i],
            reason: REASONS[i]
        }, 200)
    }

    await emit(state, 'done', { usage: {} }, 200)

    // Auf alle Entscheidungen warten — parallel, damit die Reihenfolge der
    // User-Entscheidungen egal ist.
    let applied = 0, rejected = 0, mismatched = 0
    try {
        const results = await Promise.all(
            toolCallIds.map(id => wait(state, id, 5 * 60 * 1000))
        )
        for (const info of results) {
            if (info.result === 'applied') applied++
            else if (info.result === 'version_mismatch') mismatched++
            else rejected++
        }
        const reaction = applied === count
            ? `All ${count} rewrites accepted — nice. Want me to keep going?`
            : applied === 0
                ? 'Okay, none of those landed. Want me to try a different angle?'
                : `${applied} accepted, ${rejected} rejected${mismatched ? `, ${mismatched} skipped` : ''}. Want me to revisit the rejected ones?`
        await emit(state, 'delta', { text: reaction }, 300)
        await emit(state, 'done', { usage: {} }, 100)
    } catch (err) {
        // Abort waehrend Review: AbortError an sendMessage weiterreichen —
        // erst DORT wird onAborted an die UI gemeldet. Vorher wurde er hier
        // verschluckt; onAborted kam nur, wenn der Abort zufaellig noch in
        // die Emit-Phase fiel — der Scenario-Test dazu war deshalb unter
        // Last flaky (System-Review-Beobachtung 2026-06-11).
        if (err?.name === 'AbortError') throw err
    }
}
